import { useEffect, useState } from 'react';

/**
 * usePartnerSpecs - loads the trading-partner spec roster at runtime.
 *
 * The roster (1,100+ partners) used to be compiled into the JS bundle, which
 * meant every spec change required a rebuild + redeploy by a developer. It now
 * lives in /specs-data.json next to the build, so the IXWAdmin "Update Specs"
 * tool can publish changes by writing that one file - no rebuild involved.
 * Keeping it out of the bundle also roughly halves main.js for every visitor.
 *
 * The fetch is cached at module level, so the two pages that need the roster
 * (Connections directory + SpecificationDetail) share a single request.
 */

let cache = null;      // resolved roster, kept for the life of the page
let inflight = null;   // de-dupes concurrent callers

export function loadPartnerSpecs() {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  const url = `${process.env.PUBLIC_URL || ''}/specs-data.json`;
  inflight = fetch(url, { cache: 'no-cache' })
    .then((res) => {
      if (!res.ok) throw new Error(`specs-data.json responded ${res.status}`);
      return res.json();
    })
    .then((data) => {
      // Accept either a bare array or { partners: [...] } so the admin tool can
      // add metadata (lastUpdated, updatedBy) later without breaking the site.
      const list = Array.isArray(data) ? data : data && data.partners;
      if (!Array.isArray(list)) throw new Error('specs-data.json is malformed');
      cache = list;
      inflight = null;
      return list;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });

  return inflight;
}

export default function usePartnerSpecs() {
  const [partners, setPartners] = useState(cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cache) return;              // already loaded - nothing to do
    let alive = true;
    loadPartnerSpecs()
      .then((list) => alive && setPartners(list))
      .catch((err) => alive && setError(err));
    return () => {
      alive = false;
    };
  }, []);

  return {
    partners: partners || [],
    loading: !partners && !error,
    error,
  };
}
